import { alpha } from '@mui/material/styles';

// ============================================
// SPACING & SIZING (static — theme-independent)
// ============================================
export const spacing = {
  xs: 1,
  sm: 2,
  md: 3,
  lg: 4,
  xl: 5,
};

export const borderRadius = {
  sm: 1,
  md: 2,
  lg: 3,
};

// ============================================
// TYPOGRAPHY (static — theme-independent)
// ============================================
export const typography = {
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
};

// ============================================
// TRANSITIONS (static — theme-independent)
// ============================================
export const transitions = {
  default: 'all 0.3s ease',
  smooth: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
};

// ============================================
// DYNAMIC STYLE BUILDERS
// All functions below receive the `colors` object from useAppTheme().
// ============================================

/** Form Section (Paper card) */
export const getFormSectionStyles = (colors, hasDatePicker = false) => ({
  p: 3,
  mb: 3,
  borderRadius: borderRadius.md,
  position: 'relative',
  zIndex: hasDatePicker ? 1000 : 1,
  background: colors.surface,
  border: `1px solid ${colors.surface === '#1e1e1e' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
  overflow: hasDatePicker ? 'visible' : 'hidden',
});

/** Section Title Icon */
export const getSectionIconStyles = (colors) => ({
  fontSize: '1.2rem',
  color: colors.primary,
});

/** Section Title Typography — solid color, no gradient */
export const getSectionTitleStyles = (colors) => ({
  fontWeight: typography.fontWeight.semibold,
  color: colors.primary,
});

/** Form Label */
export const getFormLabelStyles = (colors) => ({
  mb: 1,
  fontWeight: typography.fontWeight.semibold,
  color: colors.text?.secondary || colors.secondary,
});

/** Text Field Icon */
export const getTextFieldIconStyles = (colors) => ({
  fontSize: '1rem',
  color: colors.primary,
});

/** Primary Button */
export const getPrimaryButtonStyles = (colors) => ({
  borderRadius: borderRadius.md,
  fontWeight: typography.fontWeight.bold,
  background: colors.gradients?.primary,
  boxShadow: 'none',
  color: '#ffffff',
  '&:hover': {
    background: colors.gradients?.primaryHover,
    boxShadow: 'none',
  },
  '&:disabled': {
    boxShadow: 'none',
  },
});

/** Upload Button */
export const getUploadButtonStyles = (colors) => ({
  mb: 2,
  borderColor: colors.primary,
  color: colors.primary,
  borderRadius: borderRadius.md,
  fontWeight: typography.fontWeight.semibold,
  boxShadow: 'none',
  '&:hover': {
    borderColor: colors.primaryDark,
    backgroundColor: alpha(colors.primary, 0.05),
    transform: 'translateY(-1px)',
    boxShadow: 'none',
  },
  transition: transitions.default,
});

/** Image Container */
export const getImageContainerStyles = (colors) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1,
  p: 2,
  borderRadius: borderRadius.md,
  background: colors.gradients?.imageBox,
  border: `1px solid ${colors.sectionBorder}`,
  overflow: 'visible',
});

/** Image Item Box */
export const getImageItemStyles = (colors) => ({
  position: 'relative',
  borderRadius: borderRadius.md,
  overflow: 'visible',
  border: `2px solid ${colors.imageBorder}`,
  margin: '8px',
});

/** Image Avatar */
export const imageAvatarStyles = {
  width: 60,
  height: 60,
  borderRadius: borderRadius.md,
};

/** Delete Icon Button */
export const getDeleteIconButtonStyles = (colors) => ({
  position: 'absolute',
  top: -12,
  right: -12,
  backgroundColor: colors.error,
  color: 'white',
  width: 24,
  height: 24,
  zIndex: 10,
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: colors.errorDark,
    transform: 'scale(1.1)',
    boxShadow: 'none',
  },
  transition: transitions.default,
});

/** React Select Custom Styles */
export const getReactSelectStyles = (colors, hasAppraisal = false) => ({
  control: (provided, state) => ({
    ...provided,
    backgroundColor: hasAppraisal
      ? colors.background.default
      : colors.select?.controlBg || provided.backgroundColor,
    borderColor: state.isFocused
      ? colors.primary
      : colors.select?.controlBorder || provided.borderColor,
    boxShadow: state.isFocused
      ? `0 0 0 1px ${colors.primary}`
      : provided.boxShadow,
    cursor: hasAppraisal ? 'not-allowed' : 'pointer',
    fontWeight: hasAppraisal
      ? typography.fontWeight.semibold
      : provided.fontWeight,
    '&:hover': {
      borderColor: state.isFocused
        ? colors.primary
        : colors.select?.controlBorder || provided.borderColor,
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? colors.primary
      : state.isFocused
      ? alpha(colors.primary, 0.1)
      : colors.select?.menuBg || provided.backgroundColor,
    color: state.isSelected ? 'white' : colors.text.primary,
    cursor: 'pointer',
    fontWeight: state.isSelected
      ? typography.fontWeight.semibold
      : typography.fontWeight.regular,
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: colors.select?.menuBg || provided.backgroundColor,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: colors.text.primary,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: colors.select?.placeholder || colors.textMuted,
  }),
});

/** Page Header Title — solid color, clean */
export const getPageHeaderTitleStyles = (colors) => ({
  fontWeight: typography.fontWeight.bold,
  color: colors.primary,
  mb: 1,
});

/** Page Header Subtitle */
export const getPageHeaderSubtitleStyles = (colors) => ({
  fontWeight: typography.fontWeight.medium,
  color: colors.text?.secondary || colors.secondary,
});

/** Input Adornment Icon */
export const getInputAdornmentIconStyles = (colors) => ({
  mr: 1,
  color: colors.primary,
});

/** Error Alert Box */
export const getErrorAlertStyles = (colors) => ({
  fontWeight: typography.fontWeight.medium,
  backgroundColor: colors.errorLight || 'rgba(244, 67, 54, 0.1)',
  color: colors.error,
  borderRadius: borderRadius.sm,
});

/** Info Banner Box */
export const getInfoBannerStyles = (colors) => ({
  p: 2,
  borderRadius: borderRadius.md,
  background: colors.gradients?.imageBox,
  border: `1px solid ${colors.sectionBorder}`,
});

/** Disabled Text Field Styles */
export const getDisabledTextFieldStyles = (colors) => ({
  backgroundColor: colors.inputBackground || colors.background.default,
  borderRadius: borderRadius.md,
  fontWeight: typography.fontWeight.semibold,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.sectionBorder,
  },
});

/** Step Container (multi-step forms) */
export const getStepContainerStyles = (colors) => ({
  p: 4,
  borderRadius: borderRadius.lg,
  boxShadow: colors.shadows?.box,
  background: colors.surface,
});

/** Image Upload Icon Button */
export const getImageUploadIconButtonStyles = (colors) => ({
  backgroundColor: 'transparent',
  color: colors.primary,
  borderRadius: borderRadius.md,
  border: '2px solid',
  fontWeight: typography.fontWeight.semibold,
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: alpha(colors.primary, 0.05),
    boxShadow: 'none',
  },
});

/** Form Container */
export const getFormContainerStyles = (colors) => ({
  pb: 4,
  backgroundColor: 'transparent',
});

export const getLockedFieldStyles = (colors, isLocked) => {
  if (!isLocked) return {};
  return {
    backgroundColor: colors.inputBackground || colors.background.default,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.sectionBorder,
    },
  };
};

/** Clean Card/Container styles without neon or gradient wallpaper */
export const getCleanCardStyles = (isDark) => ({
  borderRadius: 3,
  background: isDark
    ? 'rgba(30, 30, 30, 0.95)'
    : 'rgba(255, 255, 255, 0.95)',
  border: isDark
    ? '1px solid rgba(255, 255, 255, 0.08)'
    : '1px solid rgba(0, 0, 0, 0.08)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  overflow: 'hidden',
});

// ============================================
// UTILITY FUNCTIONS
// ============================================

/** Get animation delay for staggered animations */
export const getAnimationDelay = (index, baseDelay = 0, increment = 0.1) =>
  baseDelay + index * increment;

/** Create a custom alpha color */
export const createAlphaColor = (color, opacity) => alpha(color, opacity);
